import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip
    // Legend
} from "recharts";

import { areaChartData } from "assets/data/area_chart";
import { useEffect, useState } from "react";
import useSize from "utils/hooks/useSize";
import { Card } from "ui";
import {
    Autocomplete,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField
} from "@mui/material";
import { countries, FullCountryObject } from "assets/data/countries";
import useWindowSize from "utils/hooks/useWindowSize";

type AreaLegendProps = {
    color: string;
    text: string;
    display: boolean;
    setDisplay: React.Dispatch<React.SetStateAction<boolean>>;
};
const AreaLegend = ({ color, text, display, setDisplay }: AreaLegendProps) => {
    return (
        <div
            className="flex gap-1 justify-center items-center"
            onClick={() => setDisplay(() => !display)}
        >
            <svg
                width="14"
                height="14"
                viewBox="0 0 32 32"
                style={{
                    display: "inline-block",
                    verticalAlign: "middle",
                    marginRight: "4px"
                }}
            >
                <title></title>
                <desc></desc>
                <path
                    stroke-width="4"
                    fill="none"
                    stroke={color}
                    d="M0,16h10.666666666666666
                                A5.333333333333333,5.333333333333333,0,1,1,21.333333333333332,16
                                H32M21.333333333333332,16
                                A5.333333333333333,5.333333333333333,0,1,1,10.666666666666666,16"
                ></path>
            </svg>
            <span
                className={`text-xs font-medium ${!display && "line-through"}`}
                style={{ color }}
            >
                {text}
            </span>
        </div>
    );
};

const AreaChartCard = () => {
    const [windowSize] = useWindowSize();
    const [width, setWidth] = useState(500);

    const [target, setTarget] = useState<HTMLDivElement | null>(null);

    const size = useSize(target);

    useEffect(() => {
        setWidth(size?.width || 500);
    }, [size]);

    // filters
    const [year, setYear] = useState("2024");
    const handleChangeYear = (event: SelectChangeEvent) => {
        setYear(event.target.value as string);
    };

    const [selectedCountry, setSelectedCountry] = useState<FullCountryObject[]>(
        []
    );

    // displayed charts
    const [displayUsers, setDisplayUsers] = useState(true);
    const [displayPosts, setDisplayPosts] = useState(true);
    const [displayIncidents, setDisplayIncidents] = useState(true);
    const [displayCommunities, setDisplayCommunities] = useState(true);

    return (
        <Card className="flex flex-col justify-center items-center gap-3">
            <div className="w-full flex justify-end items-center gap-3 my-select">
                <FormControl size="small">
                    <InputLabel id="select-year-label">Year</InputLabel>
                    <Select
                        labelId="select-year-label"
                        id="select-year"
                        value={year}
                        label="Year"
                        onChange={handleChangeYear}
                    >
                        <MenuItem value={2024}>2024</MenuItem>
                        <MenuItem value={2023}>2023</MenuItem>
                    </Select>
                </FormControl>
                {/* country */}
                <Autocomplete
                    // key={selectedCountry?.name.common || "select.country"}
                    multiple
                    disablePortal
                    value={selectedCountry}
                    onChange={(_event, newValue) => {
                        setSelectedCountry(newValue);
                    }}
                    options={countries}
                    getOptionLabel={(option: FullCountryObject) =>
                        option.name.common
                    }
                    // className="w-full"
                    renderInput={(params) => (
                        <TextField {...params} label="Select Country" />
                    )}
                    sx={{ minWidth: "250px" }}
                    size="small"
                />
            </div>
            <div ref={setTarget} className="w-full my-area-chart ">
                <div className="w-full flex flex-wrap gap-3 justify-center items-center px-4 py-2">
                    <AreaLegend
                        color="#1bace2"
                        text="Users"
                        display={displayUsers}
                        setDisplay={setDisplayUsers}
                    />
                    <AreaLegend
                        color="#44bd32"
                        text="Posts"
                        display={displayPosts}
                        setDisplay={setDisplayPosts}
                    />
                    <AreaLegend
                        color="#dc3545"
                        text="Incidents"
                        display={displayIncidents}
                        setDisplay={setDisplayIncidents}
                    />
                    <AreaLegend
                        color="#8c7ae6"
                        text="Communities"
                        display={displayCommunities}
                        setDisplay={setDisplayCommunities}
                    />
                </div>
                <AreaChart
                    width={width}
                    height={windowSize > 640 ? 400 : 250}
                    data={areaChartData}
                    margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    {/* <Legend verticalAlign="top" height={36} /> */}
                    {displayUsers ? (
                        <Area
                            type="monotone"
                            dataKey="users"
                            stroke="#1bace2"
                            fill="#1bace2"
                        />
                    ) : (
                        ""
                    )}
                    {displayPosts ? (
                        <Area
                            type="monotone"
                            dataKey="posts"
                            stroke="#44bd32"
                            fill="#44bd32"
                        />
                    ) : (
                        ""
                    )}
                    {displayIncidents ? (
                        <Area
                            type="monotone"
                            dataKey="incidents"
                            stroke="#dc3545 "
                            fill="#dc3545"
                        />
                    ) : (
                        ""
                    )}
                    {displayCommunities ? (
                        <Area
                            type="monotone"
                            dataKey="communities"
                            stroke="#8c7ae6"
                            fill="#8c7ae6"
                        />
                    ) : (
                        ""
                    )}
                </AreaChart>
            </div>
        </Card>
    );
};

export default AreaChartCard;
